import React, { useEffect, useState } from 'react';
import { leadCrm } from '../../services/dealerApi';
import { CalendarClock, PhoneCall, AlertTriangle, Loader2, Car, Home as HomeIcon, RefreshCw, CheckCircle2, XCircle, RotateCcw, UserPlus } from 'lucide-react';
import { formatUtcDatetime } from '../../utils/formatUtcDate';
import LeadUpdateModal from '../../components/dealers/LeadUpdateModal';
import ManualLeadModal from '../../components/dealers/ManualLeadModal';
import { toast } from 'sonner';

const emptyDay = {
  counts: { new_leads: 0, followups_due_today: 0, test_drives_today: 0, showroom_visits_today: 0, overdue: 0, future: 0 },
  new_leads: [],
  followups_due_today: [],
  test_drives_today: [],
  showroom_visits_today: [],
  overdue: [],
  future_by_day: {},
};

export default function MyDay() {
  const [data, setData] = useState(emptyDay);
  const [loading, setLoading] = useState(true);
  const [modalLead, setModalLead] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const load = () => {
    setLoading(true);
    leadCrm.myDay()
      .then((r) => setData(r))
      .catch((err) => toast.error(err.response?.data?.detail || err.response?.data?.detail?.message || 'Failed to load My Day'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const quickTaskStatus = async (leadId, status) => {
    try {
      await leadCrm.updateTaskStatus(leadId, { status });
      toast.success(`Marked ${status.replace(/_/g, ' ')}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) {
    return <div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-500 animate-spin" /></div>;
  }

  const { counts } = data;

  return (
    <div className="space-y-6" data-testid="my-day-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 font-bold">CRM · Today</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">My Day</h1>
          <p className="text-slate-400 text-sm mt-1">Your task-driven view — no lead is missed, no manual bucket-shuffling.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setManualOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold" data-testid="myday-add-lead">
            <UserPlus className="w-4 h-4" /> Add Lead
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-slate-300" data-testid="myday-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-center">
        <Kpi label="New" value={counts.new_leads} tone="cyan" />
        <Kpi label="Follow-ups" value={counts.followups_due_today} tone="amber" />
        <Kpi label="Test Drives" value={counts.test_drives_today} tone="blue" />
        <Kpi label="Showroom" value={counts.showroom_visits_today} tone="emerald" />
        <Kpi label="Overdue" value={counts.overdue} tone="red" pulse={counts.overdue > 0} />
        <Kpi label="Future" value={counts.future} tone="slate" />
      </div>

      {/* OVERDUE section — highlighted first */}
      {data.overdue.length > 0 && (
        <Section title="Overdue Tasks" icon={AlertTriangle} tone="red" testid="overdue-section">
          {data.overdue.map((l) => (
            <TaskRow key={l.id} lead={l} showQuickStatus onOpen={() => setModalLead(l)} onQuickStatus={quickTaskStatus} overdue />
          ))}
        </Section>
      )}

      {/* NEW LEADS */}
      {data.new_leads.length > 0 && (
        <Section title={`New Leads (${data.new_leads.length})`} icon={PhoneCall} tone="cyan" testid="new-leads-section">
          {data.new_leads.slice(0, 12).map((l) => (
            <TaskRow key={l.id} lead={l} onOpen={() => setModalLead(l)} />
          ))}
          {data.new_leads.length > 12 && (
            <div className="text-xs text-slate-500 text-center pt-1">+ {data.new_leads.length - 12} more · <a href="/dealers/marketplace-leads" className="text-cyan-400 hover:underline">Open full list</a></div>
          )}
        </Section>
      )}

      {/* FOLLOW-UPS DUE TODAY */}
      {data.followups_due_today.length > 0 && (
        <Section title={`Follow-ups Due Today (${data.followups_due_today.length})`} icon={CalendarClock} tone="amber" testid="followups-today-section">
          {data.followups_due_today.map((l) => (
            <TaskRow key={l.id} lead={l} showQuickStatus onOpen={() => setModalLead(l)} onQuickStatus={quickTaskStatus} />
          ))}
        </Section>
      )}

      {/* TEST DRIVES TODAY */}
      {data.test_drives_today.length > 0 && (
        <Section title={`Test Drives Today (${data.test_drives_today.length})`} icon={Car} tone="blue" testid="test-drives-today-section">
          {data.test_drives_today.map((l) => (
            <TaskRow key={l.id} lead={l} showQuickStatus onOpen={() => setModalLead(l)} onQuickStatus={quickTaskStatus} />
          ))}
        </Section>
      )}

      {/* SHOWROOM VISITS TODAY */}
      {data.showroom_visits_today.length > 0 && (
        <Section title={`Showroom Visits Today (${data.showroom_visits_today.length})`} icon={HomeIcon} tone="emerald" testid="showroom-today-section">
          {data.showroom_visits_today.map((l) => (
            <TaskRow key={l.id} lead={l} showQuickStatus onOpen={() => setModalLead(l)} onQuickStatus={quickTaskStatus} />
          ))}
        </Section>
      )}

      {/* FUTURE, grouped by day */}
      {Object.keys(data.future_by_day || {}).length > 0 && (
        <Section title="Future Scheduled Tasks" icon={CalendarClock} tone="slate" testid="future-section">
          {Object.entries(data.future_by_day).sort(([a],[b]) => a.localeCompare(b)).map(([day, rows]) => (
            <div key={day} className="mt-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">{new Date(day + 'T00:00:00Z').toLocaleDateString('en-IN', { dateStyle: 'medium' })}</div>
              <div className="space-y-1.5">
                {rows.map((l) => (
                  <TaskRow key={l.id} lead={l} onOpen={() => setModalLead(l)} compact />
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Empty state */}
      {counts.new_leads + counts.followups_due_today + counts.test_drives_today + counts.showroom_visits_today + counts.overdue + counts.future === 0 && (
        <div className="p-12 rounded-2xl bg-slate-900/40 border border-slate-800 text-center" data-testid="myday-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <div className="text-white font-bold text-lg">All caught up.</div>
          <div className="text-sm text-slate-400 mt-1">Nothing due today. Upload leads to get started.</div>
        </div>
      )}

      {modalLead && (
        <LeadUpdateModal
          lead={modalLead}
          onClose={(fresh) => {
            setModalLead(null);
            if (fresh) load();
          }}
        />
      )}

      <ManualLeadModal
        open={manualOpen}
        onClose={(created) => {
          setManualOpen(false);
          if (created) load();
        }}
      />
    </div>
  );
}

// ------- helpers -------

const TONE = {
  cyan: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/5',
  amber: 'text-amber-300 border-amber-500/40 bg-amber-500/5',
  blue: 'text-blue-300 border-blue-500/40 bg-blue-500/5',
  emerald: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5',
  red: 'text-red-300 border-red-500/40 bg-red-500/5',
  slate: 'text-slate-300 border-slate-700 bg-slate-900/40',
};

function Kpi({ label, value, tone = 'slate', pulse = false }) {
  return (
    <div className={`p-3 rounded-xl border ${TONE[tone]} ${pulse ? 'animate-pulse' : ''}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-2xl font-extrabold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
    </div>
  );
}

function Section({ title, icon: Icon, tone = 'cyan', testid, children }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border p-4 ${TONE[tone]}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" />
        <h2 className="text-sm font-bold uppercase tracking-wider">{title}</h2>
      </div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function TaskRow({ lead, onOpen, showQuickStatus, onQuickStatus, overdue, compact }) {
  const callHref = lead.mobile_number ? `tel:+${(lead.mobile_number + '').replace(/^\+/, '')}` : null;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border ${overdue ? 'border-red-500/50 bg-red-500/5' : 'border-slate-800 bg-slate-950/40'} ${compact ? 'p-2' : 'p-3'}`} data-testid={`task-row-${lead.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-white font-semibold">{lead.customer_name}</div>
        <div className="text-xs text-slate-400">
          {lead.interested_car || '—'}
          {lead.next_action_at && <> · <span className={overdue ? 'text-red-300 font-bold' : 'text-cyan-300'}>{formatUtcDatetime(lead.next_action_at)}</span></>}
        </div>
        {lead.notes && !compact && (
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{lead.notes}</div>
        )}
      </div>
      {lead.interest_level && (
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${lead.interest_level === 'high' ? 'bg-emerald-500/15 text-emerald-300' : lead.interest_level === 'medium' ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-700/40 text-slate-300'}`}>
          {lead.interest_level.toUpperCase()}
        </span>
      )}
      <div className="flex items-center gap-1.5">
        {showQuickStatus && lead.next_action_status !== 'completed' && lead.next_action_status !== 'no_show' && (
          <>
            <button onClick={() => onQuickStatus(lead.id, 'completed')} title="Completed" className="p-2 rounded-lg text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/30" data-testid={`quick-completed-${lead.id}`}>
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button onClick={() => onQuickStatus(lead.id, 'no_show')} title="No-show" className="p-2 rounded-lg text-red-300 hover:bg-red-500/10 border border-red-500/30" data-testid={`quick-noshow-${lead.id}`}>
              <XCircle className="w-4 h-4" />
            </button>
            <button onClick={() => onQuickStatus(lead.id, 'rescheduled')} title="Reschedule" className="p-2 rounded-lg text-amber-300 hover:bg-amber-500/10 border border-amber-500/30" data-testid={`quick-reschedule-${lead.id}`}>
              <RotateCcw className="w-4 h-4" />
            </button>
          </>
        )}
        {callHref && (
          <a href={callHref} className="p-2 rounded-lg text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30" data-testid={`call-${lead.id}`}>
            <PhoneCall className="w-4 h-4" />
          </a>
        )}
        <button onClick={onOpen} className="px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold" data-testid={`update-${lead.id}`}>
          Update
        </button>
      </div>
    </div>
  );
}
